
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Calendar, DollarSign, Coins, Plus, ChevronDown, Check, ArrowUpRight, ArrowDownRight, Clock, Loader2, Calculator, Save, Briefcase, User as UserIcon, RefreshCw, FolderOpen } from 'lucide-react';
import { Transaction } from '../types';
import { fetchCurrentPrices } from '../services/marketService';

interface AddTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (transaction: Transaction) => void;
  theme: 'dark' | 'light';
  availableSymbols: string[];
  currentUser?: string;
  transactionToEdit?: Transaction | null;
  assetType?: 'crypto' | 'stocks';
  defaultSymbol?: string;
  portfolioList?: string[];
}

const TOP_STOCKS = [
  "AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "META", "TSLA", "BRK.B", "LLY", "V", "UNH", "AVGO", "JPM", "NVO", "MA", "WMT", "XOM", "ASML", "PG", "JNJ", "HD", "ORCL", "COST", "ABBV", "MRK", "CVX", "CRM", "BAC", "KO", "PEP", "ADBE", "TMO", "LIN", "AMD", "WFC", "ACN", "MCD", "CSCO", "DIS", "ABT", "DHR", "INTU", "GE", "INTC", "VZ", "CAT", "AXP", "AMAT", "IBM", "CMCSA", "PFE", "TXN", "MS", "RTX", "QCOM", "UNP", "PM", "COP", "ISRG", "AMGN", "LOW", "HON", "SPGI", "GS", "NEE", "BKNG", "ELV", "SYK", "TJX", "LRCX", "PLD", "SBUX", "DE", "BMY", "MDLZ", "BLK", "NOW", "VRTX", "GILD", "MMC", "ADI", "LMT", "CB", "ETN", "CI", "MU", "BSX", "ZTS", "PANW", "ADP", "PGR", "C", "REGN", "REG", "FI", "ITW", "VLO", "EOG", "T", "MPC", "SLB", "SCHW", "WM", "SHW", "KLAC", "HCA", "CDNS", "MO", "PH", "CVS", "CME", "SNPS", "BDX", "APH", "FDX", "ROP", "MCO", "ECL", "EW", "MCK", "MAR", "ADSK", "MSI", "EMR", "STZ", "AIG", "TT", "PSX", "ICE", "AJG", "CMG", "MET", "TRV", "CTAS", "AON", "WELL", "MDT", "NXPI", "NEM", "HUM", "AZO", "CPRT", "KDP", "ADR", "TEL", "KMB", "DXCM", "O", "MPC", "IQV", "A", "OXY", "BK", "COF", "MCHP", "PHM", "PRU", "GEHC", "EFX", "NUE", "D", "IDXX", "DFS", "STT", "HES", "DVN", "OTIS", "F", "GD", "VRSK", "CMI", "BKR", "K", "PEG", "SRE", "CNC", "AEP", "WBD", "PARA", "PYPL", "SQ", "UUBER", "ABNB", "COIN", "DKNG", "SHOP", "RIVN", "LCID", "BILI", "Pinduoduo"
];

const POPULAR_CRYPTO = [
  "BTC", "ETH", "USDT", "BNB", "SOL", "XRP", "USDC", "ADA", "AVAX", "DOGE", "TRX", "DOT", "LINK", "MATIC", "WBTC", "DAI", "LTC", "BCH", "SHIB", "NEAR", "UNI", "APT", "LEO", "ICP", "STX", "ETC", "FIL", "ATOM", "RNDR", "OP", "IMX", "KAS", "TIA", "INJ", "VET", "MKR", "PEPE", "LDO", "CRO", "FTM", "ALGO", "SEI", "THETA", "FLOW", "GRT", "BSV", "AXS", "RUNE", "EGLD", "BEAM", "MNT", "QNT", "SAND", "AAVE", "CHZ", "SNX", "MANA", "EOS", "MINA", "IOTA", "KAVA", "GALA", "NEO", "DYDX", "WOO", "SATS", "ROSE", "PYTH", "JUP", "STRK", "WIF", "BONK", "FLOKI", "PEPE", "ORDI", "XMR", "TUSD", "FDUSD", "ZEC", "DASH", "XLM", "HBAR", "KSM", "RVN", "WAVES", "CAKE", "ZIL", "ONE", "ANKR", "ENJ", "BAT", "LRC", "CELO", "GLMR", "MOVR", "OXY", "SUSHI", "YFI", "CRV", "BAL", "COMP", "1INCH", "KNC", "LUNA", "USTC", "LUNC", "TFUEL", "HOT", "QTUM", "OMG", "ONT", "ICX", "IOST", "LSK", "STEEM", "DGB", "SC", "BTS", "XVG", "ARDR", "SYS", "SXP", "C98", "REEF", "ALICE", "TLM", "WAXP", "SKL", "CTSI", "TRB", "UMA", "BAND", "NMR", "RLC", "REP", "MLN", "STPT", "CTK", "UTK", "NKN", "DATA", "STMX", "OGN", "PUNDIX", "DENT", "WIN", "KEY", "MFT", "IOTX", "QKC", "CELR", "TROY", "VITE", "DOCK", "MBL", "STPT", "SUN", "JST", "XCH", "AR", "XYO", "DAG", "TEL", "CKB", "CEL", "EWT", "GNO", "RLY", "DAO", "POLIS", "ATLAS", "SLP", "ERN", "SUPER", "RARE", "ENS", "BICO", "SPELL", "MAGIC", "GMX", "RNDR", "AKT", "NOS", "PRIME", "RON", "DYM", "AXL", "ALT", "MANTA", "PIXEL"
];

const AddTransactionModal: React.FC<AddTransactionModalProps> = ({ 
  isOpen, onClose, onAdd, theme, availableSymbols, currentUser, transactionToEdit, assetType = 'crypto', defaultSymbol, portfolioList = ['Portafolio 1']
}) => {
  const [type, setType] = useState<'buy' | 'sell'>('buy');
  const [ticket, setTicket] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState<string>(new Date().toTimeString().split(' ')[0].substring(0, 5));
  const [targetPortfolio, setTargetPortfolio] = useState<string>(() => localStorage.getItem('cp_active_portfolio') || 'Portafolio 1');
  const [isPortfolioSelectorOpen, setIsPortfolioSelectorOpen] = useState(false);
  
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  
  const priceFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (transactionToEdit) {
        const isBuy = transactionToEdit.status.toLowerCase().includes('compra') || transactionToEdit.status.toLowerCase().includes('buy');
        setType(isBuy ? 'buy' : 'sell');
        setTicket(transactionToEdit.ticket);
        setPrice(transactionToEdit.price.toString());
        setQuantity(transactionToEdit.quantity.toString());
        const parts = transactionToEdit.date.split('/');
        if (parts.length === 3) setDate(`${parts[2]}-${parts[1]}-${parts[0]}`);
        else setDate(new Date().toISOString().split('T')[0]);
        setTime(transactionToEdit.time.substring(0, 5));
        setSearchTerm(transactionToEdit.ticket);
        setTargetPortfolio(transactionToEdit.portfolio || 'Portafolio 1');
      } else {
        const initialTicket = defaultSymbol || '';
        setTicket(initialTicket);
        setSearchTerm(initialTicket);
        setPrice('');
        setQuantity('');
        setDate(new Date().toISOString().split('T')[0]);
        setTime(new Date().toTimeString().split(' ')[0].substring(0, 5));
        setTargetPortfolio(localStorage.getItem('cp_active_portfolio') || 'Portafolio 1');
        
        if (initialTicket) {
            updatePriceForSymbol(initialTicket);
        }
      }
    }
  }, [isOpen, transactionToEdit, assetType, defaultSymbol]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updatePriceForSymbol = async (symbol: string) => {
    if (!symbol || transactionToEdit) return;
    
    setIsLoadingPrice(true);
    try {
        const cleanSymbol = symbol.split(/[-/]/)[0].toUpperCase();
        let priceValue = 0;

        if (assetType === 'crypto') {
            const binanceKey = `${cleanSymbol}USDT`;
            const prices = await fetchCurrentPrices('crypto');
            if (prices[binanceKey]) {
                priceValue = prices[binanceKey].price;
            }
        } else {
            const prices = await fetchCurrentPrices('stocks', [cleanSymbol]);
            if (prices[cleanSymbol]) {
                priceValue = prices[cleanSymbol].price;
            }
        }

        if (priceValue > 0) {
            setPrice(priceValue.toString());
        }
    } catch (error) {
        console.error("Error fetching live price for modal:", error);
    } finally {
        setIsLoadingPrice(false);
    }
  };

  const handleTicketChange = (val: string) => {
    const upperVal = val.toUpperCase();
    setTicket(upperVal);
    setSearchTerm(upperVal);
    setIsSelectOpen(true);

    if (priceFetchTimeoutRef.current) clearTimeout(priceFetchTimeoutRef.current);
    
    if (upperVal.length >= 2) {
        priceFetchTimeoutRef.current = setTimeout(() => {
            updatePriceForSymbol(upperVal);
        }, 800);
    }
  };

  const handleSelectFromList = (s: string) => {
    setTicket(s);
    setSearchTerm(s);
    setIsSelectOpen(false);
    updatePriceForSymbol(s);
  };

  const totalValue = (parseFloat(price) || 0) * (parseFloat(quantity) || 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !price || !quantity || !date || !time) return;
    
    if (!currentUser) {
        alert("Sesión expirada. Por favor, inicie sesión nuevamente.");
        return;
    }

    try {
        const timestampDate = new Date(`${date}T${time}:00`);
        if (isNaN(timestampDate.getTime())) return;
        
        const day = timestampDate.getDate().toString().padStart(2, '0');
        const month = (timestampDate.getMonth() + 1).toString().padStart(2, '0');
        const year = timestampDate.getFullYear();
        const formattedDate = `${day}/${month}/${year}`;
        
        let finalTicket = ticket.toUpperCase().trim();
        if (!finalTicket.includes('-') && !finalTicket.includes('/')) {
            finalTicket = assetType === 'stocks' ? finalTicket : `${finalTicket}-USD`;
        }
        
        const newTx: Transaction = {
          id: transactionToEdit ? transactionToEdit.id : `manual-${Date.now()}`,
          ticket: finalTicket,
          symbolName: finalTicket.split(/[-/]/)[0],
          price: parseFloat(price),
          quantity: parseFloat(quantity),
          total: totalValue,
          date: formattedDate,
          time: time + ":00",
          ticketUsdt: finalTicket.replace(/[-/]/g, '').replace('USD', 'USDT'),
          status: type === 'buy' ? 'Compra' : 'Venta',
          timestamp: Math.floor(timestampDate.getTime() / 1000),
          portfolioUser: currentUser.trim(),
          category: assetType === 'stocks' ? 'acciones' : 'criptomoneda',
          portfolio: targetPortfolio
        };
        
        onClose();
        setTimeout(() => onAdd(newTx), 0);
    } catch (err) {
      console.error(err);
    }
  };

  const getIconUrl = (symbol: string) => {
    const base = symbol.split(/[-/]/)[0].toUpperCase();
    if (assetType === 'stocks') {
      return `https://assets.parqet.com/logos/symbol/${base}?format=png`;
    }
    return `https://assets.coincap.io/assets/icons/${base.toLowerCase()}@2x.png`;
  };

  const sourceSymbols = useMemo(() => {
    if (assetType === 'stocks') {
      return Array.from(new Set([...TOP_STOCKS, ...availableSymbols.map(s => s.split(/[-/]/)[0])]));
    } else {
      return Array.from(new Set([...POPULAR_CRYPTO, ...availableSymbols.map(s => s.split(/[-/]/)[0])]));
    }
  }, [assetType, availableSymbols]);

  const filteredSymbols = useMemo(() => {
    const search = searchTerm.toUpperCase().trim();
    return sourceSymbols.filter(s => s.includes(search));
  }, [sourceSymbols, searchTerm]);

  const isCustomSymbol = searchTerm.length > 0 && !sourceSymbols.includes(searchTerm.toUpperCase());

  if (!isOpen) return null;

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/60 backdrop-blur-[4px] z-[150] flex items-center justify-center p-4 animate-in fade-in duration-500",
    container: `${theme === 'dark' ? 'bg-[#121418] border-[#2d3139]' : 'bg-white border-slate-200'} border w-full max-w-sm rounded-[2rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300`,
    header: `flex items-center justify-between p-6 pb-3 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'}`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    input: `${theme === 'dark' ? 'bg-white/[0.03] border-white/10' : 'bg-slate-50 border-slate-200'} border rounded-xl w-full px-4 py-2.5 text-[13px] font-bold transition-all outline-none placeholder:text-slate-500 font-mono`,
    label: "block text-[8px] font-black uppercase tracking-[0.2em] mb-1.5 text-indigo-500/80 pl-1",
    dropdown: `${theme === 'dark' ? 'bg-[#1c1f26] border-[#363a45]' : 'bg-white border-slate-200'} border rounded-2xl shadow-2xl absolute top-full left-0 right-0 mt-2 z-50 max-h-52 overflow-y-auto backdrop-blur-xl custom-scrollbar`
  };

  const normalBorder = theme === 'dark' ? 'border-white/10' : 'border-slate-200';
  const focusBorder = type === 'buy' ? 'focus-within:border-emerald-500/60' : 'focus-within:border-rose-500/60';

  return (
    <div className={themeClasses.overlay}>
      <div className={themeClasses.container}>
        <div className={themeClasses.header}>
          <div>
            <h2 className={`text-lg font-black tracking-tight ${themeClasses.textMain} leading-none`}>
              {transactionToEdit ? 'EDITAR TRANSACCIÓN' : 'AÑADIR TRANSACCIÓN'}
            </h2>
            <div className="relative mt-2">
               <button 
                 type="button"
                 onClick={() => setIsPortfolioSelectorOpen(!isPortfolioSelectorOpen)} 
                 className="flex items-center space-x-2 opacity-80 hover:opacity-100 transition-opacity bg-transparent border-0 p-0 focus:outline-none"
               >
                   <FolderOpen size={10} className="text-indigo-400" />
                   <span className="text-[9px] font-black uppercase">{targetPortfolio}</span>
                   <ChevronDown size={10} className="text-slate-500" />
               </button>
               {isPortfolioSelectorOpen && (
                   <div className={`absolute top-full left-0 mt-1 w-40 rounded-xl border shadow-xl z-50 overflow-hidden py-1 ${theme === 'dark' ? 'bg-[#1c1f26] border-[#363a45]' : 'bg-white border-slate-200'}`}>
                       {portfolioList.map((p, idx) => (
                           <button 
                             key={idx}
                             type="button"
                             onClick={() => { setTargetPortfolio(p); setIsPortfolioSelectorOpen(false); }} 
                             className={`w-full flex items-center justify-between px-3 py-2 text-[9px] font-black uppercase tracking-widest transition-colors ${p === targetPortfolio ? (theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600') : (theme === 'dark' ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-50 text-slate-500')}`}
                           >
                              <span>{p}</span>
                              {p === targetPortfolio && <Check size={10} />}
                           </button>
                       ))}
                   </div>
               )}
            </div>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'} transition-all active:scale-90`}>
            <X size={18} className={themeClasses.textMain} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-4">
          <div className={`grid grid-cols-2 p-1 rounded-xl ${theme === 'dark' ? 'bg-black/40' : 'bg-slate-100'}`}>
            <button type="button" onClick={() => setType('buy')} className={`flex items-center justify-center space-x-2 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${type === 'buy' ? 'bg-[#10b981] text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}><ArrowUpRight size={14} /><span>Compra</span></button>
            <button type="button" onClick={() => setType('sell')} className={`flex items-center justify-center space-x-2 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${type === 'sell' ? 'bg-[#f43f5e] text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}><ArrowDownRight size={14} /><span>Venta</span></button>
          </div>

          <div className="relative" ref={selectRef}>
            <label className={themeClasses.label}>SIMBOLO</label>
            <div className={`relative transition-all duration-300 rounded-xl border ${normalBorder} ${focusBorder} overflow-hidden`}>
               <div className="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/5 flex items-center justify-center overflow-hidden border border-white/5 pointer-events-none">
                  <span className="text-[10px] font-black text-indigo-400 uppercase">{ticket.charAt(0) || '?'}</span>
                  <img src={getIconUrl(ticket)} className="absolute inset-0 w-full h-full object-contain p-1 bg-inherit" alt="" onError={(e) => (e.currentTarget.style.display = 'none')} />
               </div>
               <input
                 type="text"
                 placeholder="Ej: BTC, AAPL..."
                 value={ticket}
                 onChange={(e) => handleTicketChange(e.target.value)}
                 onFocus={() => setIsSelectOpen(true)}
                 className={`w-full bg-transparent pl-12 pr-10 py-2.5 text-[13px] font-bold outline-none placeholder:text-slate-500 font-mono ${themeClasses.textMain}`}
               />
               <ChevronDown size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-transform ${isSelectOpen ? 'rotate-180' : ''}`} />
            </div>

            {isSelectOpen && (
              <div className={themeClasses.dropdown}>
                <div className="py-1">
                  {isCustomSymbol && (
                    <button type="button" onClick={() => handleSelectFromList(searchTerm.toUpperCase())} className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-indigo-600/10 transition-colors border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'}`}>
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500"><Plus size={12} strokeWidth={4} /></div>
                      <span className={`text-[11px] font-black ${themeClasses.textMain}`}>Usar: "{searchTerm.toUpperCase()}"</span>
                    </button>
                  )}
                  {filteredSymbols.map(s => (
                    <button key={s} type="button" onClick={() => handleSelectFromList(s)} className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-indigo-600/10 transition-colors ${ticket === s ? 'bg-indigo-600/20' : ''}`}>
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center relative overflow-hidden"><img src={getIconUrl(s)} className="absolute inset-0 w-full h-full object-contain p-1" alt="" onError={(e) => (e.currentTarget.style.display = 'none')} /><span className="text-[8px] font-black text-indigo-400">{s.charAt(0)}</span></div>
                        <span className={`text-[11px] font-black ${ticket === s ? 'text-indigo-400' : themeClasses.textMain}`}>{s}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={themeClasses.label}>Cantidad</label>
              <div className={`relative rounded-xl border ${normalBorder} ${focusBorder}`}>
                <Briefcase size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="number" step="any" placeholder="0.00" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={`w-full bg-transparent pl-10 pr-4 py-2.5 text-[13px] font-bold outline-none font-mono ${themeClasses.textMain}`} required />
              </div>
            </div>
            <div>
              <label className={themeClasses.label}>Precio</label>
              <div className={`relative rounded-xl border ${normalBorder} ${focusBorder}`}>
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="number" step="any" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} className={`w-full bg-transparent pl-10 pr-10 py-2.5 text-[13px] font-bold outline-none font-mono ${themeClasses.textMain}`} required />
                {isLoadingPrice && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <RefreshCw size={14} className="text-indigo-500 animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={themeClasses.label}>Fecha</label>
              <div className={`relative rounded-xl border ${normalBorder} ${focusBorder}`}>
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`w-full bg-transparent pl-10 pr-4 py-2.5 text-[13px] font-bold outline-none font-mono ${themeClasses.textMain}`} style={{ colorScheme: theme }} required />
              </div>
            </div>
            <div>
              <label className={themeClasses.label}>Hora</label>
              <div className={`relative rounded-xl border ${normalBorder} ${focusBorder}`}>
                <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={`w-full bg-transparent pl-10 pr-4 py-2.5 text-[13px] font-bold outline-none font-mono ${themeClasses.textMain}`} style={{ colorScheme: theme }} required />
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-2xl border ${theme === 'dark' ? 'bg-indigo-500/[0.02] border-indigo-500/10' : 'bg-slate-50 border-slate-100'} text-center`}>
            <span className="text-[7px] font-black uppercase text-indigo-500 tracking-[0.2em]">Cálculo Total</span>
            <div className={`text-xl font-black font-mono tracking-tighter ${themeClasses.textMain}`}>$ {totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>

          <button type="submit" className={`w-full py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[10px] text-white transition-all shadow-lg active:scale-95 flex items-center justify-center space-x-2 ${type === 'buy' ? 'bg-[#10b981]' : 'bg-[#f43f5e]'}`}>
            {transactionToEdit ? <Save size={16} /> : <Plus size={16} />}
            <span>{transactionToEdit ? 'Actualizar' : 'Guardar'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddTransactionModal;